// Editorial content for CCDR — pulled from the supplied content portfolio (June 2026).
// Australian English spelling throughout (Centre, organisation, colour, programme).

export const PORTFOLIO_LAST_REVIEWED = "June 2026";

export const SITE = {
  name: "Centre for Climate and Disaster Resilience",
  acronym: "CCDR",
  tagline: "Resilience, decided with evidence.",
  contactEmail: "info@ccdr.au",
  longDescription:
    "Contemporary, evidence based insight on climate and disaster resilience for policymakers, enterprises, and researchers worldwide, anchored to the frameworks and standards that matter.",
};

export const PEAK_BODIES = [
  { name: "United Nations", short: "UN" },
  { name: "UNDRR", short: "UNDRR" },
  { name: "World Meteorological Organization", short: "WMO" },
  { name: "International Telecommunication Union", short: "ITU" },
  { name: "OECD", short: "OECD" },
  { name: "IEEE", short: "IEEE" },
  { name: "International Federation of Red Cross and Red Crescent Societies", short: "IFRC" },
  { name: "UNICEF", short: "UNICEF" },
  { name: "UNESCO", short: "UNESCO" },
];

export const FOCUS_AREAS = [
  {
    slug: "climate-adaptation",
    title: "Climate Resilience and Adaptation",
    short: "Planning, finance, and nature based approaches that help systems absorb and adapt to climate change.",
    long:
      "Adaptation is the work of helping systems — cities, supply chains, ecosystems, and communities — absorb shocks and adjust to a changing climate. This pillar covers climate risk assessment, adaptation planning, resilient infrastructure, nature based solutions, and the finance that makes adaptation possible, including the question of loss and damage where prevention is no longer enough. It connects directly to the Paris Agreement and its global goal on adaptation, and to the growing body of national adaptation planning that translates global ambition into local action.",
    icon: "leaf",
  },
  {
    slug: "drr-early-warning",
    title: "Disaster Risk Reduction and Early Warning",
    short: "The Sendai Framework, multi hazard early warning, and anticipatory action that saves lives.",
    long:
      "Disaster risk reduction shifts the emphasis from response to prevention. This pillar is anchored to the Sendai Framework for Disaster Risk Reduction, with its focus on understanding risk, strengthening governance, investing in resilience, and improving preparedness and recovery. It gives particular attention to multi hazard early warning — the subject of the United Nations Early Warnings for All initiative, which aims to ensure that every person on Earth is protected by a life saving early warning system by the end of 2027. Recent monitoring shows that around six in ten countries now report having a multi hazard early warning system, yet coverage remains thin in small island developing states and least developed countries.",
    icon: "radio-tower",
  },
  {
    slug: "ethical-ai",
    title: "Ethical AI for Climate and Disaster Resilience",
    short: "How to use artificial intelligence responsibly across modelling, warning, and response.",
    long:
      "Artificial intelligence is moving quickly into climate modelling, hazard forecasting, early warning delivery, and disaster response. Used well, it extends reach and speed. Used carelessly, it introduces new forms of fragility, bias, and environmental cost. This pillar sets out how to adopt AI responsibly across the resilience lifecycle, drawing on the OECD AI Principles, the IEEE standards, the NIST AI Risk Management Framework, and the ISO management standard for AI.",
    icon: "cpu",
    deepLink: "/ethical-ai",
  },
  {
    slug: "standards-governance",
    title: "Standards, Governance and Interoperability",
    short: "The standards that make resilience and technology trustworthy and able to work together.",
    long:
      "Standards are what make resilience and technology trustworthy and able to work together across institutions and borders. This pillar tracks the standards landscape — from the IEEE 7000 series on value based engineering to the ISO management system standard for AI and the common alerting protocols that let warnings cross networks. The emphasis is interoperability, because a standard that cannot connect to its neighbours protects no one.",
    icon: "scale",
  },
  {
    slug: "data-sovereignty",
    title: "Data Sovereignty and Indigenous Knowledge",
    short: "Respecting who holds data and knowledge, including First Nations data sovereignty.",
    long:
      "Resilience runs on data, and data carries questions of ownership, control, and consent. This pillar covers data governance for resilience and gives specific attention to Indigenous and First Nations data sovereignty, including the CARE Principles for Indigenous Data Governance — collective benefit, authority to control, responsibility, and ethics. It also makes the case that local and traditional knowledge is not a supplement to scientific early warning but an essential part of it, a lesson visible in many of the most effective community level systems.",
    icon: "users",
  },
  {
    slug: "innovation-foresight",
    title: "Innovation, Foresight and Emerging Technology",
    short: "Where the frontier is heading, from earth observation to digital twins and beyond.",
    long:
      "This pillar looks ahead. It covers earth observation and remote sensing, digital twins of cities and coastlines, anticipatory and forecast based finance, advanced numerical and AI weather models, and the connectivity innovations — including satellite based alerting — that extend warnings to communities beyond mobile coverage. The purpose is foresight: helping leaders see where capability is heading so they can govern it before it arrives rather than after.",
    icon: "telescope",
  },
];

export const FIVE_PRINCIPLES = [
  {
    name: "Independence",
    body: "The Centre references peak bodies as authoritative sources and does not act as a vendor for any single technology or political position.",
  },
  {
    name: "Evidence",
    body: "Every claim is traceable to a credible source, and living content carries a visible review date.",
  },
  {
    name: "Interoperability",
    body: "Frameworks and standards are most useful when they work together, so the Centre reads them as a connected system rather than in isolation.",
  },
  {
    name: "Equity",
    body: "Resilience must reach the most exposed and respect those who hold local and Indigenous knowledge.",
  },
  {
    name: "Foresight",
    body: "The Centre looks ahead to where governance and innovation are heading, not only where they stand today.",
  },
];

export const THREE_ROLES = [
  {
    name: "Observes",
    body:
      "Watching the development of frameworks, standards, and innovations across the multilateral and standards communities.",
  },
  {
    name: "Translates",
    body:
      "Turning dense instruments into clear guidance for policymakers, enterprises, and researchers.",
  },
  {
    name: "Convenes",
    body:
      "Bringing decision makers together so that knowledge moves between sectors and borders rather than staying locked inside any one institution.",
  },
];

export const AUDIENCES = [
  {
    slug: "policymakers",
    label: "Policymakers and regulators",
    body:
      "Ministers and senior public servants come for a current, citable synthesis of the instruments they are accountable to and a path to participate in policy dialogues.",
    cta: "Request a briefing",
  },
  {
    slug: "enterprise",
    label: "Enterprise and risk leaders",
    body:
      "Executives and chief risk officers come to understand obligations and opportunities across resilience and responsible technology before they become liabilities.",
    cta: "Start the maturity assessment",
  },
  {
    slug: "research",
    label: "Researchers and universities",
    body:
      "Research leads come for connective tissue between disciplines, visibility through the knowledge hub, and partnership on convenings and applied projects.",
    cta: "Propose a collaboration",
  },
  {
    slug: "tech",
    label: "Technology leaders",
    body:
      "Technology community leaders come to understand how to deploy capability responsibly, grounded in recognised standards, and a forum to shape how governance and resilience meet.",
    cta: "Join the dialogue",
  },
];

export const ETHICAL_AI_LIFECYCLE = [
  {
    step: "01",
    title: "Frame and govern",
    body:
      "Define the resilience outcome and the people affected, and establish accountability before any model is built. The NIST AI Risk Management Framework — govern, map, measure, manage — gives a recognised structure for this.",
  },
  {
    step: "02",
    title: "Source data responsibly",
    body:
      "Confront questions of ownership, consent, and representativeness at the outset, applying data sovereignty principles — including the CARE Principles where Indigenous data is involved.",
  },
  {
    step: "03",
    title: "Build to recognised values",
    body:
      "Use value based engineering practices such as the IEEE 7000 series so that human wellbeing and ecological sustainability are treated as design requirements, not afterthoughts.",
  },
  {
    step: "04",
    title: "Validate against the extremes",
    body:
      "Resilience systems are judged on rare and severe events. Models must be stress tested where they are most likely to fail and least likely to have training data.",
  },
  {
    step: "05",
    title: "Manage as a system",
    body:
      "Treat AI as an ongoing management responsibility using ISO/IEC 42001, the management system standard that makes governance auditable rather than aspirational.",
  },
  {
    step: "06",
    title: "Account for the footprint",
    body:
      "Measure and reduce the environmental cost of AI itself — an area where new standards work on the environmental impact of AI is now emerging.",
  },
];

export const FRAMEWORKS_LIBRARY = [
  // group: climate-disaster
  {
    slug: "sendai",
    name: "Sendai Framework for Disaster Risk Reduction 2015–2030",
    body: "UNDRR. The global blueprint for reducing disaster risk, built on four priorities and seven targets. Its midterm review in 2023 found progress but a persistent gap between rising risk and the investment made to prevent it. Target G, on early warning, is the bridge to the Early Warnings for All initiative.",
    group: "climate-disaster",
    body_short: "UNDRR’s global blueprint for reducing disaster risk: four priorities, seven targets.",
  },
  {
    slug: "paris",
    name: "Paris Agreement",
    body: "UNFCCC. The binding international treaty on climate change, covering both mitigation and adaptation, and home to the global goal on adaptation that frames much resilience planning.",
    group: "climate-disaster",
    body_short: "UNFCCC. Covers mitigation and adaptation; home to the global goal on adaptation.",
  },
  {
    slug: "ewfa",
    name: "Early Warnings for All",
    body: "WMO, UNDRR, ITU, IFRC. A United Nations initiative to ensure every person on Earth is protected by a multi hazard early warning system by the end of 2027, structured around four pillars from risk knowledge to communication and response.",
    group: "climate-disaster",
    body_short: "WMO/UNDRR/ITU/IFRC. Multi-hazard EWS for every person on Earth by end-2027.",
  },
  {
    slug: "sdgs",
    name: "Sustainable Development Goals",
    body: "United Nations. The seventeen goals to 2030. Resilience touches many of them, with Goal 13 on climate action and Goal 11 on sustainable cities most directly engaged.",
    group: "climate-disaster",
    body_short: "17 goals to 2030. Goals 13 and 11 most directly engaged.",
  },
  // group: tech-ai
  {
    slug: "oecd-ai",
    name: "OECD AI Principles",
    body: "OECD. The first intergovernmental standard for AI, adopted in 2019 and updated since, calling for trustworthy AI in pursuit of beneficial outcomes for people and the planet.",
    group: "tech-ai",
    body_short: "First intergovernmental standard for AI. Trustworthy AI for people and the planet.",
  },
  {
    slug: "global-digital-compact",
    name: "Global Digital Compact",
    body: "United Nations. Adopted within the Pact for the Future in 2024, it sets a shared vision for an open, safe, and inclusive digital future and led to the new United Nations mechanisms for AI governance.",
    group: "tech-ai",
    body_short: "Adopted in 2024. Vision for an open, safe, inclusive digital future.",
  },
  {
    slug: "un-ai-mechanisms",
    name: "Independent Scientific Panel and Global Dialogue on AI",
    body: "United Nations. Two mechanisms established in 2025 to give the world an evidence based scientific assessment of AI and an inclusive forum to govern it, with the first Global Dialogue session in Geneva in 2026.",
    group: "tech-ai",
    body_short: "2025: a scientific Panel and a Global Dialogue. First session Geneva 2026.",
  },
  {
    slug: "hiroshima",
    name: "Hiroshima AI Process",
    body: "G7 with OECD. A code of conduct and reporting framework for organisations developing advanced AI systems, providing a practical transparency mechanism.",
    group: "tech-ai",
    body_short: "G7 + OECD code of conduct for advanced AI developers.",
  },
  {
    slug: "eu-ai-act",
    name: "EU AI Act",
    body: "European Union. The first comprehensive, risk-based regulation of AI, with provisions phasing in across 2025 and 2026. Increasingly a reference point for organisations operating across borders — including for AI used in early warning systems and critical infrastructure.",
    group: "tech-ai",
    body_short: "Phasing in 2025–2026. Risk-based, with cross-border reach.",
  },
  // group: standards
  {
    slug: "ieee-7000",
    name: "IEEE 7000 series",
    body: "IEEE. A family of standards on ethically aligned and value based engineering for autonomous and intelligent systems, prioritising people and the planet as measures of success.",
    group: "standards",
    body_short: "Value-based engineering for autonomous and intelligent systems.",
  },
  {
    slug: "ieee-p7021",
    name: "IEEE P7021 — ethical AI in long term climate projections",
    body: "IEEE. A recommended practice for implementing ethical artificial intelligence in long term climate projections towards sustainability, addressing how AI is embedded in climate modelling.",
    group: "standards",
    body_short: "How to embed ethics into AI used for long-term climate projection.",
  },
  {
    slug: "ieee-ai-env",
    name: "IEEE work on the environmental impact of AI",
    body: "IEEE. Emerging standardisation focused on metrics for the environmental footprint of AI, an issue increasingly material to climate credibility.",
    group: "standards",
    body_short: "Emerging metrics for the environmental footprint of AI.",
  },
  {
    slug: "iso-42001",
    name: "ISO/IEC 42001",
    body: "ISO and IEC. The management system standard for artificial intelligence, which makes responsible AI auditable by setting out how an organisation governs AI across its lifecycle.",
    group: "standards",
    body_short: "Management system standard for AI. Makes responsible AI auditable.",
  },
  {
    slug: "nist-ai-rmf",
    name: "NIST AI Risk Management Framework",
    body: "NIST. A widely adopted voluntary framework structured around govern, map, measure, and manage, giving organisations a practical way to handle AI risk. A critical-infrastructure profile is in development in 2026, translating the framework for owners and operators of water, energy and emergency communications.",
    group: "standards",
    body_short: "Govern, Map, Measure, Manage — with a 2026 critical-infrastructure profile in development.",
  },
  {
    slug: "care",
    name: "CARE Principles for Indigenous Data Governance",
    body: "Global Indigenous Data Alliance. Collective benefit, authority to control, responsibility, and ethics — the principles that complement open data with respect for Indigenous data sovereignty.",
    group: "standards",
    body_short: "Collective benefit · Authority to control · Responsibility · Ethics.",
  },
  {
    slug: "itu-cap",
    name: "ITU standards and Common Alerting Protocol",
    body: "ITU. Telecommunication standards, including alerting protocols, that allow warnings to travel reliably across networks and reach people at risk.",
    group: "standards",
    body_short: "Standards that let warnings travel reliably across networks.",
  },
  {
    slug: "au-ai-ethics",
    name: "Australia's AI Ethics Principles",
    body: "Australian Government (Department of Industry, Science and Resources). Eight voluntary principles — human-centred values, fairness, privacy, reliability, transparency, contestability, accountability, and human, societal and environmental wellbeing — guiding AI use across Australian government and industry.",
    group: "tech-ai",
    body_short: "Eight voluntary principles for AI use across Australia.",
  },
  {
    slug: "au-cyber-strategy",
    name: "Australian Cyber Security Strategy 2023–2030",
    body: "Australian Government. Sets six 'cyber shields' including resilient critical infrastructure and trusted technology, with implications for AI in emergency communications and essential services.",
    group: "tech-ai",
    body_short: "Six cyber shields, including resilient critical infrastructure.",
  },
  {
    slug: "us-executive-orders-ai",
    name: "US Federal AI policy direction",
    body: "United States Government. Successive Executive Orders and OMB memoranda set safety, security, and transparency expectations for federal AI use — with the NIST AI RMF as the primary technical reference.",
    group: "tech-ai",
    body_short: "Federal AI direction anchored to the NIST AI RMF.",
  },
  {
    slug: "eu-cra",
    name: "EU Cyber Resilience Act",
    body: "European Union. Sets mandatory cybersecurity requirements for products with digital elements placed on the EU market — increasingly relevant where resilience systems integrate connected devices and AI.",
    group: "tech-ai",
    body_short: "Cybersecurity requirements for digital products on the EU market.",
  },
  {
    slug: "iso-37120-smart-city",
    name: "ISO 37120 — Sustainable cities and communities",
    body: "ISO. Indicators for city services and quality of life, supporting consistent measurement of resilience, sustainability, and smart-city performance across jurisdictions.",
    group: "standards",
    body_short: "City indicators for resilience, sustainability, and smart-city performance.",
  },
];

export const FRAMEWORK_GROUPS = [
  { slug: "climate-disaster", label: "Climate and Disaster Resilience" },
  { slug: "tech-ai", label: "Technology and AI Governance" },
  { slug: "standards", label: "Standards that Connect Them" },
];

export const SDGS_ANCHOR = [
  {
    n: 13,
    name: "Climate Action",
    body: "The anchor goal. Resilience is the practical expression of its targets on strengthening adaptive capacity to climate hazards and on improving education, awareness, and early warning.",
  },
  {
    n: 11,
    name: "Sustainable Cities and Communities",
    body: "Cities concentrate both people and risk. Target 11.5 calls for reducing the deaths and economic losses caused by disasters, and target 11.b calls for cities to adopt and implement disaster risk reduction strategies in line with the Sendai Framework.",
  },
];

export const SDGS_WIDER = [
  { n: 1, name: "No Poverty", body: "Disasters push people into poverty and trap them there. Resilience and anticipatory action protect livelihoods before losses occur." },
  { n: 2, name: "Zero Hunger", body: "Climate shocks disrupt food systems. Early warning and forecast based action help protect harvests and food security." },
  { n: 6, name: "Clean Water and Sanitation", body: "Floods and droughts are water hazards first. Resilient water management sits at the centre of adaptation." },
  { n: 7, name: "Affordable and Clean Energy", body: "Resilient and clean energy systems keep warning, response, and recovery running when hazards strike." },
  { n: 9, name: "Industry, Innovation and Infrastructure", body: "Resilient infrastructure and responsible innovation, including AI, are the backbone of modern resilience." },
  { n: 10, name: "Reduced Inequalities", body: "Risk falls hardest on the most exposed. Equitable resilience means leaving no one without protection or warning." },
  { n: 14, name: "Life Below Water", body: "Coastal and marine systems both buffer hazards and face them. Protecting them is protecting resilience." },
  { n: 15, name: "Life on Land", body: "Healthy ecosystems are nature based infrastructure, reducing flood, fire, and erosion risk." },
  { n: 16, name: "Peace, Justice and Strong Institutions", body: "Resilience depends on capable, trusted institutions and sound governance, including the governance of technology." },
  { n: 17, name: "Partnerships for the Goals", body: "No single body delivers resilience. The Centre's convening role is itself an expression of this goal." },
];

export const PARTNERSHIPS = [
  {
    group: "Multilateral and UN system",
    members: [
      { name: "United Nations", body: "Through the Sustainable Development Goals, the Global Digital Compact, and the new mechanisms for AI governance, the United Nations sets the overarching agenda within which resilience sits." },
      { name: "UNDRR", body: "Custodian of the Sendai Framework and co lead of the Early Warnings for All initiative, and the primary reference for disaster risk reduction." },
      { name: "WMO", body: "The specialised agency for weather, climate, and water, and co lead of Early Warnings for All, central to forecasting and hazard monitoring." },
      { name: "ITU", body: "Supports Early Warnings for All on warning dissemination and connectivity, and convenes the AI for Good community, linking technology to resilience." },
      { name: "UNESCO", body: "Brings science, education, and culture to resilience, including ocean science, heritage protection, and the ethics of artificial intelligence." },
      { name: "UNICEF", body: "Focuses on children, among the most exposed to climate and disaster risk, and on the data and systems that protect them." },
    ],
  },
  {
    group: "Humanitarian network",
    members: [
      { name: "IFRC", body: "A support pillar of Early Warnings for All and a leader in anticipatory and forecast based action, connecting global frameworks to community level response." },
    ],
  },
  {
    group: "Standards and technology governance",
    members: [
      { name: "OECD", body: "Author of the first intergovernmental AI principles and host of expert work on AI, compute, and climate, shaping how governments approach trustworthy AI." },
      { name: "IEEE", body: "Through its standards association and technology and climate work, IEEE develops the value based engineering standards and the climate specific AI practices that the Centre draws on." },
      { name: "ISO and IEC", body: "Develop the international management system standards, including the standard for artificial intelligence that makes responsible AI auditable." },
      { name: "NIST", body: "Provider of the widely adopted AI Risk Management Framework, a practical reference for organisations managing AI risk." },
    ],
  },
  {
    group: "Conservation and environment",
    members: [
      { name: "IUCN — International Union for Conservation of Nature", body: "The global authority on the status of the natural world and the measures needed to safeguard it. The IUCN Global Standard for Nature-based Solutions is the Centre's reference for ecosystem-based adaptation." },
      { name: "The Nature Conservancy", body: "Applied science and finance for nature-based resilience across coasts, forests, and freshwater systems — with active programmes across multiple regions." },
      { name: "WWF International", body: "Policy and field work on biodiversity, climate, and water — with strong national offices around the world." },
      { name: "Conservation International", body: "Science, partnership, and field implementation linking biodiversity, climate, and human wellbeing." },
    ],
  },
];

export const GLOSSARY = [
  { term: "Adaptation", body: "Adjusting systems and behaviour to reduce harm from a changing climate, as distinct from mitigation, which reduces the emissions causing climate change." },
  { term: "Anticipatory action", body: "Acting on a forecast before a hazard strikes, so that resources reach people while there is still time to reduce harm." },
  { term: "CARE Principles", body: "Principles for Indigenous data governance — collective benefit, authority to control, responsibility, and ethics — which complement open data with respect for Indigenous rights." },
  { term: "Disaster risk reduction", body: "The practice of preventing new risk, reducing existing risk, and managing residual risk, rather than only responding after a disaster." },
  { term: "Early Warnings for All", body: "A United Nations initiative to ensure that every person on Earth is protected by a multi hazard early warning system by the end of 2027." },
  { term: "Loss and damage", body: "The harm from climate impacts that cannot be prevented by adaptation, and the question of how it is addressed and financed." },
  { term: "Multi hazard early warning system", body: "A system that detects multiple types of hazard and turns that knowledge into timely, understandable warnings that reach the people at risk." },
  { term: "Nature based solutions", body: "Using healthy ecosystems, such as wetlands and mangroves, as infrastructure that reduces hazard risk while delivering wider benefits." },
  { term: "Responsible AI", body: "The design, deployment, and management of artificial intelligence in ways that are trustworthy, fair, transparent, accountable, and environmentally sustainable." },
  { term: "Sendai Framework", body: "The global agreement, running from 2015 to 2030, that guides how the world reduces disaster risk." },
  { term: "Sovereignty in data and AI", body: "The principle that a community or nation retains meaningful authority over the data and AI systems that affect it, including the institutions and standards that govern them." },
];

export const MATURITY_DOMAINS = [
  {
    key: "governance",
    label: "Governance & accountability",
    prompt: "How clearly does your organisation assign accountability for resilience and AI decisions?",
  },
  {
    key: "data",
    label: "Data sovereignty & sourcing",
    prompt: "How rigorously are ownership, consent, and representativeness handled across your data?",
  },
  {
    key: "values",
    label: "Value-based engineering",
    prompt: "How explicitly are human wellbeing and ecological sustainability treated as design requirements?",
  },
  {
    key: "validation",
    label: "Validation against extremes",
    prompt: "How well are systems stress-tested against rare and severe events?",
  },
  {
    key: "management",
    label: "Auditable AI management",
    prompt: "How auditable is your AI governance across the lifecycle (ISO/IEC 42001 alignment)?",
  },
  {
    key: "footprint",
    label: "Environmental footprint",
    prompt: "How systematically do you measure and reduce the environmental cost of AI itself?",
  },
];

export const MATURITY_SCALE = [
  { value: 0, label: "Initial", desc: "Ad-hoc or absent." },
  { value: 1, label: "Developing", desc: "Beginning to formalise." },
  { value: 2, label: "Defined", desc: "Documented and consistent." },
  { value: 3, label: "Managed", desc: "Measured and reviewed." },
  { value: 4, label: "Optimising", desc: "Continuously improved and auditable." },
];


// ---------------------------------------------------------------------------
// Enhanced edition (mid-2026 currency refresh) additions
// ---------------------------------------------------------------------------

// UN AI governance architecture — full block for Responsible AI page
export const UN_AI_ARCHITECTURE = {
  title: "The new global AI governance layer",
  body:
    "Following the Global Digital Compact, adopted within the Pact for the Future in 2024, the United Nations has stood up two new mechanisms. The Independent International Scientific Panel on Artificial Intelligence provides annual, evidence-based scientific assessments. The Global Dialogue on AI Governance is the inclusive forum in which governments and stakeholders work through how AI should be governed. Its first session is being held in Geneva in July 2026, alongside the ITU AI for Good Summit.",
  why_matters:
    "The questions that drive global AI governance — safety, transparency, and closing divides — are the same questions that determine whether AI strengthens or weakens resilience.",
};

// Standards Watch — emerging items the Centre is actively tracking
export const STANDARDS_WATCH = [
  {
    body_name: "NIST",
    item: "AI RMF — Critical Infrastructure Profile (2026)",
    status: "In progress",
    note: "The 2026 profile work translates the govern–map–measure–manage functions for owners and operators of critical infrastructure, including water, energy, and emergency communications.",
  },
  {
    body_name: "United Nations",
    item: "Global Dialogue on AI Governance — Session 01",
    status: "Live · Geneva, July 2026",
    note: "First convening of the Global Dialogue, alongside the ITU AI for Good Summit. Independent International Scientific Panel report expected in parallel.",
  },
  {
    body_name: "EU",
    item: "AI Act — phased application",
    status: "In application",
    note: "Risk-based provisions phasing in across 2025–2026. Increasingly a reference point for organisations operating across borders, including for AI used in early warning and infrastructure.",
  },
  {
    body_name: "G7 + OECD",
    item: "Hiroshima AI Process — code of conduct & reporting",
    status: "Active",
    note: "Practical transparency mechanism for organisations developing advanced AI systems.",
  },
  {
    body_name: "IEEE",
    item: "Sustainable AI standards work",
    status: "Emerging",
    note: "Standardisation around metrics for the environmental footprint of AI — energy, water, and material cost — becoming material to climate credibility.",
  },
  {
    body_name: "ISO/IEC",
    item: "42001 — adoption across resilience-critical organisations",
    status: "Active",
    note: "Management system standard for AI; increasingly the audit basis for responsible AI in regulated sectors.",
  },
  {
    body_name: "WMO + UNDRR + ITU + IFRC",
    item: "Early Warnings for All — Global Status 2025",
    status: "Active",
    note: "Latest Global Status of Multi-Hazard Early Warning Systems: ~6 in 10 countries report a MHEWS, gap concentrates in SIDS and LDCs.",
  },
];

// Environmental footprint of AI
export const AI_FOOTPRINT = {
  title: "The footprint of AI",
  body:
    "AI itself has a growing material cost — energy at training and inference, water for cooling, embedded carbon in hardware. For an institution making climate claims, the credibility of those claims now depends on whether the footprint of the AI it deploys is measured and reduced, not assumed away. IEEE standards work on sustainable AI is emerging precisely to provide the metrics.",
};

// CARE Principles — deeper treatment
export const CARE_PRINCIPLES = {
  title: "Data and AI sovereignty",
  body:
    "Resilience runs on data, and data carries questions of who decides. The CARE Principles for Indigenous Data Governance — Collective benefit, Authority to control, Responsibility, and Ethics — complement open-data principles with respect for Indigenous and First Nations rights. National data sovereignty raises parallel questions for governments asking how AI infrastructure is provisioned, where models are trained, and who can be held to account.",
  pillars: [
    { letter: "C", name: "Collective benefit", body: "Data should be designed and used so that Indigenous Peoples derive benefit from it." },
    { letter: "A", name: "Authority to control", body: "Indigenous Peoples’ rights and interests in data must be recognised and their authority respected." },
    { letter: "R", name: "Responsibility", body: "Those working with Indigenous data have a responsibility to share how data is used to support self-determination." },
    { letter: "E", name: "Ethics", body: "Rights and wellbeing should be the primary concern at all stages of the data lifecycle." },
  ],
};

// Responsible AI Adoption Framework — 7 lifecycle stages
export const RAI_ADOPTION_FRAMEWORK = [
  { step: "01", title: "Public value problem framing", body: "Define the resilience outcome, the people affected, and the standard of accountability before any model is selected." },
  { step: "02", title: "Data readiness, provenance & sovereignty", body: "Source data lawfully and ethically. Apply data sovereignty principles, including the CARE Principles where Indigenous data is involved." },
  { step: "03", title: "Model development & uncertainty", body: "Use value-based engineering (IEEE 7000 series). Make assumptions, uncertainties, and known limitations legible." },
  { step: "04", title: "Validation & assurance", body: "Stress-test against rare, severe events. Align with NIST AI RMF map–measure functions and IEEE P7021 for climate projection cases." },
  { step: "05", title: "Human oversight & operational integration", body: "Keep meaningful human judgement in the loop, especially where warnings, eligibility, or rights are at stake." },
  { step: "06", title: "Deployment, monitoring & incident learning", body: "Treat monitoring and incident review as ongoing obligations, not launch-day checkboxes." },
  { step: "07", title: "Accountability, transparency & footprint", body: "Manage as a system under ISO/IEC 42001. Measure and reduce the environmental cost of the AI itself." },
];

// Foresight Observatory
export const FORESIGHT_SIGNALS = [
  { tag: "Governance", title: "UN Global Dialogue on AI Governance opens in Geneva", note: "First session held alongside the ITU AI for Good Summit, July 2026. Independent Scientific Panel report expected in parallel." },
  { tag: "Standards", title: "NIST AI RMF — Critical Infrastructure Profile (USA)", note: "Sector-specific profile translating govern–map–measure–manage for owners and operators of US critical infrastructure." },
  { tag: "Governance", title: "EU AI Act — high-risk provisions in force", note: "Risk-based obligations applying to providers placing AI on the EU market, with material relevance to AI in early warning and infrastructure." },
  { tag: "Smart cities", title: "European Mission for Climate-Neutral and Smart Cities", note: "100 European cities working toward climate neutrality by 2030, providing live test-beds for AI-assisted urban resilience." },
  { tag: "Climate", title: "AI-native weather models cross operational threshold", note: "Skilful forecasts at lower compute than traditional NWP; integration into the Bureau of Meteorology, NOAA, ECMWF, and others accelerating." },
  { tag: "Sovereignty", title: "Indigenous data governance enters public-sector AI procurement", note: "Australian and US jurisdictions referencing CARE Principles in public-sector AI procurement and First Nations data agreements." },
  { tag: "Footprint", title: "Sustainable AI metrics gain traction", note: "IEEE and EU initiatives moving toward standardised measurement of the energy and water cost of AI." },
  { tag: "Disaster", title: "Early Warnings for All midpoint", note: "Global Status of MHEWS 2025 confirms progress, with coverage gaps in SIDS and LDCs — and continued investment in early warning systems worldwide." },
  { tag: "Convergence", title: "Climate-tech and smart-city budgets converge", note: "Major public investment programmes worldwide are funding AI, sensors, and grid modernisation as one stack." },
];

export const FORESIGHT_SCENARIOS = [
  {
    name: "Convergent governance",
    body: "UN, OECD, IEEE, and ISO/IEC instruments increasingly interoperate. Organisations that mapped their AI estate to NIST/ISO 42001 early sit comfortably in audit conversations.",
  },
  {
    name: "Sovereignty bifurcation",
    body: "National and Indigenous data sovereignty claims drive divergent regional rules on training data. Cross-border resilience systems depend on careful federation design.",
  },
  {
    name: "Footprint accounting",
    body: "Climate credibility demands a measured AI footprint. Procurement starts to require sustainable-AI disclosures alongside performance benchmarks.",
  },
];

export const STANDARDS_HORIZON = [
  { period: "Now → 6 months", items: ["First UN Global Dialogue on AI Governance outputs", "NIST AI RMF critical-infrastructure profile drafts", "Continued phased EU AI Act application"] },
  { period: "6 → 18 months", items: ["IEEE sustainable-AI metrics formalisation", "Updated WMO/UNDRR Early Warnings for All status reporting", "Maturing ISO/IEC 42001 implementation guidance"] },
  { period: "18 → 36 months", items: ["Interoperable AI assurance regimes", "Indigenous data sovereignty embedded in public procurement", "Climate-projection AI conformance to IEEE P7021 widely referenced"] },
];

// Executive Education
export const EXEC_EDUCATION_PROGRAMMES = [
  {
    name: "Resilience leadership for ministers and senior public servants",
    audience: "Government",
    body: "A short-form programme covering Sendai, the Paris Agreement, Early Warnings for All, and the new UN AI governance mechanisms — built around the decisions a minister or senior public servant will actually be asked to take.",
    cadence: "Intake by invitation",
  },
  {
    name: "Boards and chief risk officers: responsible AI for resilience",
    audience: "Enterprise",
    body: "How to govern AI use across resilience-critical operations — from the NIST AI RMF and ISO/IEC 42001 to value-based engineering and footprint accounting. Practical, audit-ready.",
    cadence: "Quarterly cohorts",
  },
  {
    name: "Research-to-practice dialogues",
    audience: "Research",
    body: "Working sessions for university and institute leads to surface evidence into policy and standards conversations — with the convening rhythm built around the global calendar.",
    cadence: "Three per year",
  },
  {
    name: "Standards-aware technology leadership",
    audience: "Technology",
    body: "For CTOs and heads of engineering deploying AI in early warning, response, and adaptation — making IEEE 7000, IEEE P7021, ISO/IEC 42001, and the EU AI Act actionable.",
    cadence: "On demand",
  },
];

// Toolkits & templates (Resources page additions)
export const TOOLKITS = [
  { name: "AI-for-Resilience readiness checklist", body: "A short, board-level checklist covering the seven stages of the Responsible AI Adoption Framework." },
  { name: "Sendai-to-implementation checklist", body: "How to translate the four Sendai priorities into accountable national and institutional action." },
  { name: "Standards mapping template", body: "Map an AI or resilience capability to NIST AI RMF, ISO/IEC 42001, IEEE 7000 and the relevant climate frameworks." },
  { name: "Early warning governance checklist", body: "Governance, dissemination and reach questions to test a multi-hazard early warning system against Early Warnings for All." },
];

export const TEMPLATES = [
  { name: "Policy brief template", body: "The Centre’s standard structure: framing, evidence, options, leadership implications, sources." },
  { name: "Source evidence register", body: "Track the authoritative source behind every claim, with last-reviewed dates." },
  { name: "AI impact assessment prompts", body: "Plain-language prompts to test an AI deployment against rights, sovereignty, and footprint considerations." },
  { name: "Risk communication planning worksheet", body: "Designed for warning agencies and humanitarian partners — language, channel, reach, and equity." },
];

// Three maturity model dimensions
export const MATURITY_MODELS = {
  resilience: {
    key: "resilience",
    label: "Resilience governance",
    description: "Locate institutional capability to govern climate and disaster resilience.",
    domains: [
      { key: "risk_understanding", label: "Risk understanding", prompt: "How completely does your institution understand its exposure across hazards and supply chains?" },
      { key: "governance", label: "Governance & accountability", prompt: "How clearly is accountability for resilience decisions assigned?" },
      { key: "investment", label: "Investment in prevention", prompt: "How systematically is investment shifted from response to prevention?" },
      { key: "preparedness", label: "Preparedness & recovery", prompt: "How prepared is the institution to manage and learn from events?" },
    ],
  },
  ai_assurance: {
    key: "ai_assurance",
    label: "AI assurance",
    description: "Locate your AI estate against recognised standards for responsible adoption.",
    domains: [
      { key: "governance", label: "Governance & accountability", prompt: "How clearly does your organisation assign accountability for AI decisions?" },
      { key: "data", label: "Data sovereignty & sourcing", prompt: "How rigorously are ownership, consent, and representativeness handled?" },
      { key: "values", label: "Value-based engineering", prompt: "How explicitly are human wellbeing and ecological sustainability design requirements?" },
      { key: "validation", label: "Validation against extremes", prompt: "How well are systems stress-tested against rare and severe events?" },
      { key: "management", label: "Auditable AI management", prompt: "How auditable is governance across the lifecycle (ISO/IEC 42001 alignment)?" },
      { key: "footprint", label: "Environmental footprint", prompt: "How systematically do you measure and reduce the environmental cost of AI?" },
    ],
  },
  standards_alignment: {
    key: "standards_alignment",
    label: "Standards alignment",
    description: "Locate your alignment with the standards that connect resilience and technology.",
    domains: [
      { key: "frameworks", label: "Framework awareness", prompt: "How well do staff understand the global frameworks (Sendai, Paris, SDGs, GDC)?" },
      { key: "standards", label: "Standards adoption", prompt: "How systematically are NIST AI RMF, ISO/IEC 42001 and IEEE practices adopted?" },
      { key: "interop", label: "Interoperability", prompt: "How well do your tools and warnings work across institutional and national boundaries?" },
      { key: "procurement", label: "Procurement & audit", prompt: "How explicitly do procurement and audit reference recognised standards?" },
    ],
  },
};

// Contact pathway extensions
export const CONTACT_PATHWAYS = [
  { value: "general", label: "General enquiry" },
  { value: "policymakers", label: "Policymakers and government" },
  { value: "enterprise", label: "Enterprise and risk leaders" },
  { value: "research", label: "Researchers and universities" },
  { value: "tech", label: "Technology leaders" },
  { value: "partnership", label: "Partnership proposal" },
  { value: "media", label: "Media request" },
  { value: "speaking", label: "Speaking invitation" },
  { value: "briefing", label: "Executive briefing" },
  { value: "standards", label: "Standards engagement" },
];

// Smart-cities & convergence — woven through the home and focus areas
export const CONVERGENCE_THEMES = [
  { name: "Smart climate action", body: "AI-assisted forecasting, anticipatory finance, and grid modernisation that make climate ambition operational." },
  { name: "Resilient smart cities", body: "From climate-neutral cities missions to smart-grid programmes and net-zero precincts around the world." },
  { name: "Trusted AI for public value", body: "Aligning AI deployment with NIST, ISO/IEC 42001, IEEE 7000 series, the EU AI Act, and Australia's AI Ethics Principles." },
  { name: "Sustainability of technology", body: "Measuring and reducing the energy, water, and material footprint of the AI that resilience now depends on." },
];

// Climate-Smart Initiatives video series — Media page
export const MEDIA_SERIES = {
  title: "Climate-Smart Initiatives",
  presenter: "Dr Mahendra Samarawickrama",
  dek:
    "A seven-part video series at the intersection of climate resilience, biodiversity, and responsible digital technology — built with and for First Nations communities, and resonant for any jurisdiction asking how AI, IoT, and citizen science can serve community-led climate action.",
  playlistUrl: "https://www.youtube.com/playlist?list=PLZSc8UgsqGCmKEY5pK2qYS4wal27N7X2J",
};

export const MEDIA_VIDEOS = [
  {
    id: "_Tlyo_dOF8I",
    title: "IoT Awards 2021 — Diversity, Equity and Inclusion in Action (Finalist)",
    summary:
      "Recognition for a programme demonstrating how Internet of Things technology can serve diversity, equity, and inclusion outcomes for First Nations communities facing climate risk.",
    tag: "Recognition",
    duration: "2 min",
  },
  {
    id: "psySmdBc0lE",
    title: "Inclusion of First Nations Peoples in Climate-Smart Programmes",
    summary:
      "The case for designing climate-smart programmes with First Nations Peoples rather than for them — and what that requires of governance, data, and technology choices.",
    tag: "Programme design",
    duration: "8 min",
  },
  {
    id: "1vE25Y4f_xc",
    title: "Climate Red Summit — Mitigating Risks for First Nations Peoples (Ignite Talk)",
    summary:
      "An Ignite talk from the Climate Red Summit on the disproportionate climate risk borne by First Nations Peoples and the responses that are working.",
    tag: "Talk",
    duration: "5 min",
  },
  {
    id: "cc5Y0X0qXos",
    title: "A Novel Citizen-Scientist Project for First Nations Peoples",
    summary:
      "A community-led citizen-science approach to climate-risk mitigation — combining traditional knowledge with sensor networks and open data.",
    tag: "Citizen science",
    duration: "6 min",
  },
  {
    id: "BnX5RLkuc9c",
    title: "A Mobile Application for First Nations Peoples to Mitigate Climate Risks",
    summary:
      "How a purpose-built mobile application supports communities to understand, report, and act on the climate hazards they face.",
    tag: "Digital tool",
    duration: "4 min",
  },
  {
    id: "vIb5pmiJCjM",
    title: "Climate Collective — Mitigating the Risk of Climate Change to First Nations Peoples",
    summary:
      "Inside the Climate Collective: a coalition approach to surfacing risk, sharing knowledge, and coordinating practical action across communities.",
    tag: "Coalition",
    duration: "7 min",
  },
  {
    id: "J0PGqY9BVU0",
    title: "Climate Change and Biodiversity — Cane Toad Invasion (Queensland to NSW)",
    summary:
      "Using the cane toad's range expansion as a case study for the wider biodiversity impacts of a warming climate — and what monitoring can do about it.",
    tag: "Biodiversity",
    duration: "9 min",
  },
];


// Open Data Sources — curated registry of authoritative datasets across climate,
// disaster, earth observation, environment, flood, and hydrology. Adapted from
// the ICPAC open data registry, retained for our EU/AU/US audience.
export const OPEN_DATA_GROUPS = [
  {
    slug: "climate-data",
    label: "Climate Data",
    sources: [
      { name: "ICPAC website", url: "http://www.icpac.net/", desc: "Rainfall, temperature, and 10-day/monthly/seasonal forecasts. Coverage: ICPAC region." },
      { name: "PERSIANN Rainfall Data", url: "ftp://persiann.eng.uci.edu/CHRSdata/PERSIANN-CCS/daily/", desc: "Daily rainfall estimates at 0.25° resolution (60°S–60°N) from 1983 to near-present via PERSIANN-CDR neural-network estimates." },
      { name: "RFE Rainfall Data (NOAA CPC)", url: "https://earlywarning.usgs.gov/fews/product/48", desc: "Dekadal (10-day) Rainfall Estimate from NOAA's Climate Prediction Center. Coverage: Africa." },
      { name: "CHIRPS Rainfall", url: "https://www.chc.ucsb.edu/data/chirps", desc: "Climate Hazards Group InfraRed Precipitation with Station data — 35+ year quasi-global rainfall dataset, 50°S–50°N, 1981 to near-present." },
      { name: "ECMWF S2S Database", url: "https://apps.ecmwf.int/datasets/data/s2s/levtype=sfc/type=cf/", desc: "Sub-seasonal and seasonal climate forecast data. Coverage: Global." },
      { name: "Earth System Grid Federation (ESGF)", url: "https://esgf.llnl.gov/", desc: "Climate model simulations including CMIP5, CMIP6, and CORDEX. Coverage: Global." },
      { name: "KNMI Climate Explorer", url: "https://climexp.knmi.nl/start.cgi", desc: "Aggregator of climate data with analysis tools, hosted by the Royal Netherlands Meteorological Institute." },
      { name: "IMERG Rainfall (NASA)", url: "https://gpm.nasa.gov/data/imerg", desc: "Integrated Multi-satellitE Retrievals for GPM — NASA's global rainfall database." },
      { name: "GFS Forecast Rainfall (NOAA)", url: "https://ftp.cpc.ncep.noaa.gov/GIS/gfs_0.25/", desc: "NOAA NCEP Global Forecast System: dozens of atmospheric and land-soil variables, including temperature, winds, precipitation, soil moisture, and ozone." },
      { name: "US Government Open Data — Climate", url: "https://catalog.data.gov/dataset?q=climate", desc: "377+ climate datasets in the US open-data initiative, paired with the US Climate Resilience Toolkit and Climate Explorer." },
      { name: "NASA Earth Observatory", url: "https://earthobservatory.nasa.gov/", desc: "Earth-science data spanning 16 climate factors (aerosols, temperature, carbon) back to 1999. Includes NASA Earth Observations (NEO)." },
      { name: "ESA Climate Change Initiative (CCI)", url: "http://cci.esa.int/data", desc: "European Space Agency CCI — 40+ years of merged satellite climate observations." },
      { name: "ICPAC Data Library and Maproom", url: "http://digilib.icpac.net/", desc: "Maps and figures monitoring climate, agriculture, and health conditions across the ICPAC region. Variables include precipitation, temperature, growing-season dates, SPI." },
      { name: "Copernicus Climate Data Store (CDS)", url: "https://cds.climate.copernicus.eu/", desc: "The Copernicus Climate Change Service (C3S) flagship: authoritative information on past, present and future climate in Europe and worldwide." },
      { name: "SCIPEA Climate Data Portal", url: "http://197.254.113.170/maproom/", desc: "Africa-coverage maproom for GCM hindcasts and forecasts, monitoring climate, agriculture, and health conditions." },
      { name: "IRI Maproom and Data Library", url: "https://iridl.ldeo.columbia.edu/maproom/index.html", desc: "Columbia IRI's data library — hundreds of terabytes of climate data; variables include precipitation, temperature, heatwaves, ocean temperature, S2S forecasts. Coverage: Global." },
    ],
  },
  {
    slug: "disaster",
    label: "Disaster Risk Management",
    sources: [
      { name: "DesInventar — Disaster Loss Database", url: "https://www.desinventar.net/", desc: "Conceptual and methodological tool for building national disaster inventories — damage, losses, and the broader effects of disasters. Coverage: Global." },
      { name: "INFORM — Index for Risk Assessment", url: "https://drmkc.jrc.ec.europa.eu/inform-index", desc: "Open-source global risk assessment for humanitarian crises and disasters; supports prevention, preparedness, and response decisions." },
      { name: "UN-SPIDER Data Sources", url: "http://www.un-spider.org/links-and-resources/data-sources", desc: "Space-based information for disaster management and emergency response." },
      { name: "EM-DAT — Emergency Events Database", url: "https://www.emdat.be/", desc: "Core data on the occurrence and effects of 22,000+ mass disasters worldwide from 1900 to the present, compiled from UN, NGO, insurance and research sources." },
    ],
  },
  {
    slug: "earth-observation",
    label: "Earth Observation",
    sources: [
      { name: "NASA Earthdata Search", url: "https://search.earthdata.nasa.gov/", desc: "Online access to NASA's Earth Observing System Data and Information System (EOSDIS) collections." },
      { name: "Remote Pixel Search", url: "https://search.remotepixel.ca/", desc: "Discover satellite imagery; pair with the Remote Pixel Viewer for band combinations, image enhancement, and basemaps." },
    ],
  },
  {
    slug: "environment",
    label: "Environmental Monitoring Data",
    sources: [
      { name: "SM2RAIN Datasets", url: "http://hydrology.irpi.cnr.it/download-area/sm2rain-data-sets/", desc: "Rainfall products derived from satellite soil-moisture data via the SM2RAIN algorithm (Brocca et al., 2014)." },
      { name: "ICPAC Geoportal", url: "http://geoportal.icpac.net/", desc: "ICPAC's spatial data and visualisation hub." },
      { name: "GMES & Africa Geoportal", url: "http://gmes.icpac.net/data-center/vgt-ndvi", desc: "Forest, agriculture, rangeland, and protected-area monitoring across the ICPAC region. Datasets include LULC, LULC change, NDVI, CHIRPS, FEWSNET, DMP, fAPAR." },
      { name: "Copernicus Services", url: "https://www.copernicus.eu/en/access-data", desc: "Sentinel imagery and EO products across Land, Marine, Atmosphere, Climate, Emergency, and Security services. Coverage: Global." },
      { name: "VITO Remote Sensing & EO Data", url: "https://www.vito-eodata.be/PDF/portal/Application.html", desc: "Global EO data and products including NDVI at 100m / 300m / 1km." },
      { name: "Sentinel Hub", url: "https://www.sentinel-hub.com/", desc: "Cloud API to explore ESA Sentinel imagery." },
      { name: "Copernicus Open Access Hub", url: "https://scihub.copernicus.eu/", desc: "Find and download Sentinel images for any area of interest." },
      { name: "VITO Satellite Imagery", url: "https://remotesensing.vito.be/data-products-services/eo-data-made-easily-accessible", desc: "Sensor archive: Sentinel-1, Sentinel-2, MetOp, MERIS, PROBA-V, SPOT-V." },
      { name: "USGS Earth Explorer", url: "https://earthexplorer.usgs.gov/", desc: "USGS satellite imagery explorer." },
      { name: "UNEP Environmental Data Explorer", url: "https://geodata.grid.unep.ch/", desc: "500+ variables across agriculture, CO₂ emissions, disasters, endangered species, energy, GHGs, methane, N₂O, population, and protected areas." },
      { name: "FAO GeoNetwork", url: "http://www.fao.org/geonetwork", desc: "FAO's geographic data network — 247 climate datasets plus satellite imagery, interactive maps and tools. Topics: biodiversity hotspots, carbon sequestration, cropland expansion, deforestation, Köppen classification, protected areas." },
      { name: "NASA SEDAC", url: "https://sedac.ciesin.columbia.edu/", desc: "NASA's Socioeconomic Data and Applications Center — 123 datasets on conservation and climate including deforestation, drought, EPI, human footprint, species richness, sea-level rise, sustainability indices." },
      { name: "OECD Environment Data", url: "https://data.oecd.org/environment.htm", desc: "OECD knowledge hub for climate-data analysis: forest resources, GHGs, green households, population, waste, water withdrawal." },
      { name: "UNEP/GRID-Arendal Graphics", url: "https://www.grida.no/resources/8422", desc: "Free-to-reuse graphic resources on ecosystem services and climate-change impacts (health, agriculture, landscape)." },
      { name: "USGS Evapotranspiration Data", url: "https://earlywarning.usgs.gov/fews/product/465", desc: "Global dekadal actual evapotranspiration data." },
    ],
  },
  {
    slug: "flood",
    label: "Flood Maps",
    sources: [
      { name: "NASA Near-Real-Time Global Flood Mapping", url: "https://floodmap.modaps.eosdis.nasa.gov/", desc: "Near-real-time NASA flood mapping. Coverage: Africa." },
      { name: "WRI Aqueduct", url: "https://www.wri.org/aqueduct", desc: "World Resources Institute's water-risk suite: Aqueduct Water Risk Atlas, Country Rankings, Food, and Floods." },
      { name: "GloFAS — Global Flood Awareness System", url: "https://www.globalfloods.eu/", desc: "Global hydrological forecast and monitoring system, jointly developed by the European Commission and ECMWF — independent of administrative or political boundaries." },
    ],
  },
  {
    slug: "hydrology",
    label: "Hydrological Data",
    sources: [
      { name: "Hydroweb (Theia)", url: "http://hydroweb.theia-land.fr/", desc: "Time series of water levels in the world's rivers and lakes." },
      { name: "Dartmouth Flood Observatory", url: "https://www.dartmouth.edu/~floods/", desc: "Space-based measurement of surface water for research, education, and humanitarian use." },
      { name: "DAHITI — Database for Hydrological Time Series", url: "https://dahiti.dgfi.tum.de/en/", desc: "TU München-DGFI satellite-derived water-level and hydrological data on lakes, reservoirs, rivers, and wetlands." },
    ],
  },
];

// Adapted from ICPAC's open data registry (icpac.net/open-data-sources) — June 2026.
